// ============================================================================
//  RESUME CONTENT — source of truth for the entire portfolio.
//  Edit values here to update the site. No component edits needed.
// ============================================================================

export const profile = {
  name: "Mihir Nadkarni",
  firstName: "Mihir",
  lastName: "Nadkarni",
  title: "Customer Success Leader",
  location: "Mumbai, India",
  tagline: "Retention, growth & advocacy for enterprise B2B SaaS.",
  intro:
    "Dynamic, AI-ready Customer Success leader with 12+ years' experience in B2B SaaS across India, APAC and EMEA. I've managed a $14M portfolio — driving retention, expansion and onboarding through data-led success plans, cross-functional programs and customer advocacy.",
  aboutLong:
    "I partner with enterprise leaders across Banking, Fintech, Gaming, E-commerce, Edtech, Travel-tech, IT and Consulting to turn product adoption into measurable business value. Whether it's building churn-forecasting models, automating QBR frameworks, or mentoring high-performing CSM teams, my focus is the same: secure revenue, grow accounts, and make customers advocates.",
  email: "nadkarni_mihir@yahoo.co.in",
  phone: "+91 97024 06333",
  linkedin: "https://www.linkedin.com/in/mihir-nadkarni/",
};

// Headline stats shown in the About / metrics strip.
export const metrics = [
  { value: "$14M", label: "Portfolio managed" },
  { value: "12+", label: "Years in B2B SaaS" },
  { value: "93%", label: "ARR retention" },
  { value: "$300K", label: "Avg. quarterly upsell" },
];

// Numbered manifesto chapters — the "how I work" narrative.
export const manifesto = [
  {
    no: "01",
    title: "Retention is a discipline, not an event.",
    body: "Renewals are won months in advance. I build churn-forecasting models and structured success plans so risk is visible early and value realization is deliberate — not a scramble at contract-end.",
  },
  {
    no: "02",
    title: "AI is a force multiplier.",
    body: "I embed AI agents — Copilot, Claude Co-Work and custom synthesizers — into the CS motion. One automation cut business-review prep by five days. Efficiency compounds into consistency at CXO level.",
  },
  {
    no: "03",
    title: "Advocacy compounds.",
    body: "Happy customers are the best growth engine. I've program-managed 15+ case studies and 30+ G2 reviews, turning outcomes into stories that shorten every future sales cycle.",
  },
  {
    no: "04",
    title: "Teams outperform heroes.",
    body: "I've led 11 CSMs managing 85 enterprise accounts. Hiring, onboarding and mentoring the right people scales impact far beyond any single portfolio.",
  },
];

// Experience — most recent first.
export const experience = [
  {
    company: "Adobe",
    role: "Solution Account Manager — India BFSI Key & Enterprise",
    period: "Jan 2026 — Present",
    location: "Mumbai, India",
    points: [
      "Manage a $4.2M portfolio across 15 enterprise accounts — accountable for usage adoption, retention, renewal and post-onboarding upsell.",
      "Drove 100%+ net dollar retention and expansion in two quarters: renewed $1M book with incremental ACV of $840K ($440K volume/license upgrade, $400K new product stream).",
      "Built an automated, scalable QBR framework for 15+ enterprise customers — competitive benchmarking, peer use-cases and a data-driven 3–6 month value roadmap, powered by Adobe AI agents, Copilot and Claude Co-Work.",
      "Managed the Adobe partner ecosystem (DWAO, Customer Centria, DEPT, Idealake) to deliver 12 enterprise CXO-level strategic reviews.",
    ],
  },
  {
    company: "CleverTap",
    role: "Associate Director — Customer Success (Head, India West Key & Enterprise)",
    period: "Jan 2024 — Jan 2026",
    location: "Mumbai, India",
    points: [
      "Led a team of 11 CSMs managing a $14M portfolio across 85 enterprise accounts — 22% of CleverTap's revenue.",
      "Built and deployed a churn-forecasting model enabling 300 business reviews & 275 success plans (2024–25): $300K avg. quarterly upsell, 93% ARR retention, 30% PAS growth, 27% NPS growth.",
      "Drove customer advocacy — secured 30+ G2 reviews and 15 case studies.",
      "Designed a structured onboarding & training program for new joiners with a 5-member core team across Sales, HR, Product and Engineering.",
      "Created a CSV synthesizer agent for campaign performance tracking — cut business-review prep by 5 days (83% higher efficiency).",
    ],
  },
  {
    company: "CleverTap",
    role: "Senior CSM / CSM",
    period: "Jan 2019 — Jan 2023",
    location: "Mumbai, India",
    points: [
      "Managed 20 enterprise accounts (avg. 2-year tenure) — delivered 75 business reviews and 60 success plans.",
      "Expanded portfolio from $2.5M to $4M, protecting $300K in at-risk revenue.",
      "Boosted NPS by 40% and PAS by 20% through targeted success planning.",
      "Handled strategic logos: Dream11, Rapido, Kotak Bank, MPL, Ajio, UpGrad, Axis Bank, Zepto, Khatabook, SonyLIV.",
    ],
  },
  {
    company: "Bitgenie Research",
    role: "Co-founder",
    period: "May 2018 — Jan 2019",
    location: "Mumbai, India",
    points: [
      "Led customer acquisition, growth hacking and onboarding — secured two key clients worth ₹50L as key account manager for conversion and support.",
    ],
  },
  {
    company: "Novartis",
    role: "Multi-channel Project Manager",
    period: "Oct 2014 — May 2018",
    location: "Hyderabad, India",
    points: [
      "Managed digital marketing and channel projects across 15 countries (APAC & Europe) in a global, multicultural team.",
      "Drove business development, digital consulting and vendor management — averaging $50K per quarter.",
    ],
  },
  {
    company: "Boehringer Ingelheim",
    role: "Assistant Product Manager — Marketing",
    period: "May 2013 — Oct 2014",
    location: "Mumbai, India",
    points: [
      "Managed social media and ATL/BTL promotions for key products with a ₹15L budget — digital strategy, vendor management and analytics.",
    ],
  },
  {
    company: "Infosys",
    role: "Systems Engineer — Finacle ERP",
    period: "Aug 2009 — Jun 2011",
    location: "Mumbai, India",
    points: [
      "Customized 50+ post-production critical line items for Finacle Universal Banking Solutions and front-end web applications.",
    ],
  },
];

// Selected projects — shown under the Work section.
export const projects = [
  {
    title: "Job-fit with Resume",
    blurb:
      "An AI tool that instantly scores how well a resume matches a job description and surfaces the gaps to close.",
    tag: "AI · Product",
    url: "https://job-fit-quick.emergent.host/",
  },
  {
    title: "Book Exchange - Swap, Lend and Discover",
    blurb:
      "A community-driven concept for swapping and discovering books — turning idle shelves into a sharing network.",
    tag: "Prototype",
    url: "https://book-swap-lend-exchange.netlify.app/",
  },
];

export const skillGroups = [
  {
    label: "Leadership",
    items: [
      "Customer Success Leadership",
      "Enterprise Account Management",
      "Key Account Management",
      "Team Building & Mentoring",
    ],
  },
  {
    label: "Growth & Retention",
    items: [
      "SaaS Retention",
      "Churn Forecasting",
      "Upsell & Cross-sell",
      "Customer Advocacy",
      "QBR Frameworks",
    ],
  },
  {
    label: "AI & Tools",
    items: [
      "Copilot & Claude Co-Work",
      "Replit · Cursor · Lovable",
      "Process Automation",
      "Microsoft Suite",
    ],
  },
];

// Client logos / marquee words.
export const clients = [
  "Dream11", "Rapido", "Kotak Bank", "MPL", "Ajio", "UpGrad",
  "Axis Bank", "Zepto", "Khatabook", "SonyLIV", "RBL Bank",
  "PharmEasy", "ICICI Bank",
];

export const awards = [
  {
    org: "CleverTap",
    title: "MVP Award (Q2 2023) & Star Award (Q1 2021)",
    detail:
      "Drove $200K avg. quarterly upsell per client; program-managed advocacy case studies from Dream11, MPL, RBL Bank, PharmEasy and ICICI Bank.",
  },
  {
    org: "Novartis",
    title: "Silver Star (Jun 2015, Dec 2017)",
    detail:
      "Consistently positive reviews from global & Europe initiatives — 50% revenue growth and +0.4 CSAT.",
  },
  {
    org: "NMIMS",
    title: "MBA Gold Medal & Certificate of Honor",
    detail:
      "Awarded for all-round performance (3.65 CGPA, Dean's List). Published research at ICBM Singapore.",
  },
];

export const certifications = [
  "Great Manager Institute — iLead (2025)",
  "Be10x — Generative AI for Microsoft Office",
  "CleverTap University Certified (2021)",
  "Digital Vidya — Certified Digital Marketing Master (VSkills, Govt. of India)",
];

export const education = [
  {
    school: "Narsee Monjee Institute of Management Studies",
    degree: "MBA, Marketing",
    year: "2013",
    note: "Gold Medal · 7th on Dean's List · 3.65 CGPA",
  },
  {
    school: "Fr. Conceicao Rodrigues College of Engineering",
    degree: "B.E., Computer Science",
    year: "2009",
    note: "First Class (68.1%)",
  },
];

// ============================================================================
//  WRITING — plug in your own Substack / Medium URLs here.
//  Replace `url: "#"` with your real post link and update title/excerpt.
// ============================================================================
export const writing = [
  {
    title: "Why churn forecasting beats churn reaction",
    excerpt:
      "A practical look at building a lightweight risk model that gives your CS team a 90-day head start on every renewal.",
    tag: "Retention",
    url: "https://medium.com/@mihirnadkarnicsm87/the-90-day-head-start-forecasting-churn-instead-of-reacting-to-it-41462f407974",
  },
  {
    title: "The AI-native CSM: agents in the success motion",
    excerpt:
      "How I use Copilot, Claude and custom synthesizers to turn days of QBR prep into minutes — without losing the human touch.",
    tag: "AI in CS",
    url: "https://medium.com/@mihirnadkarnicsm87/the-ai-native-csm-agents-in-the-success-motion-5577db6397fa",
  },
  {
    title: "I built a 20-month operating system for myself",
    excerpt:
      "Here's what's inside — the habits, rituals and guardrails I use to stay focused, keep growing, and do my best work.",
    tag: "Reflections",
    url: "https://medium.com/@mihirnadkarnicsm87/i-built-a-20-month-operating-system-for-myself-heres-what-s-in-it-851a680aaa27",
  },
];

// ============================================================================
//  VIDEO — paste your own YouTube/Vimeo embed link below.
//  Set `videoEmbedUrl` to your embed URL (e.g. https://www.youtube.com/embed/VIDEO_ID)
//  Leave it null to show the styled placeholder with instructions.
// ============================================================================
export const videoEmbedUrl = "https://www.youtube.com/embed/e-ZsFJXphII";
